import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { automunityAPI } from '../lib/api';
import './Chat.css';

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'amy';
  timestamp: Date;
}

const Chat: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [searchParams] = useSearchParams();
  const promptHandled = useRef(false);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const initialMessage: Message = {
      id: '1',
      text: "Hey! I'm AMY, your Automunity assistant. I can help you with questions about our automations, submit requests, check health status, and more. What can I help you with?",
      sender: 'amy',
      timestamp: new Date()
    };
    setMessages([initialMessage]);

    // Handle ?prompt= parameter — only once
    if (!promptHandled.current) {
      const prompt = searchParams.get('prompt');
      if (prompt) {
        promptHandled.current = true;
        const cleanPrompt = prompt.replace(/^"|"$/g, '');
        setTimeout(() => {
          sendMessage(cleanPrompt);
        }, 500);
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const sendMessage = async (textToSend: string) => {
    if (!textToSend || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      text: textToSend,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputText('');
    setIsLoading(true);
    setError(null);

    try {
      // Build conversation history for context
      const history = messages
        .filter(m => m.id !== '1') // exclude initial greeting
        .map(m => ({
          role: m.sender === 'user' ? 'user' : 'assistant',
          content: m.text
        }));

      const response = await automunityAPI.chatWithAmy(textToSend, history);
      const result = await response.body.json() as any;

      let amyText = '';
      if (result.response) {
        amyText = result.response;
      } else if (result.records) {
        const records = typeof result.records === 'string' ? JSON.parse(result.records) : result.records;
        amyText = records.response || records.message || JSON.stringify(records);
      } else {
        amyText = "I received your message but couldn't generate a response. Please try again.";
      }

      const amyMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: amyText,
        sender: 'amy',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, amyMessage]);
    } catch (err: any) {
      console.error('AMY chat error:', err);
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: "I'm having trouble reaching my backend right now. Please try again in a moment, or check automunity.dev.itrucksack.com directly.",
        sender: 'amy',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
      setError(err.message || 'Failed to reach AMY backend');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async (messageText?: string) => {
    const textToSend = messageText || inputText.trim();
    if (textToSend) sendMessage(textToSend);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="chat-container">
      <div className="chat-header">
        <h1>Chat with AMY</h1>
        <p>Your Automunity AI assistant</p>
      </div>
      
      <div className="chat-messages">
        {messages.map((message) => (
          <div key={message.id} className={`message ${message.sender}`}>
            <div className="message-avatar">
              {message.sender === 'amy' ? '🤖' : '👤'}
            </div>
            <div className="message-content">
              <div className="message-text">
                {message.text.split('\n').map((line, index) => (
                  <div key={index}>
                    {line.startsWith('•') || line.startsWith('**') || line.includes('**') ? (
                      <div dangerouslySetInnerHTML={{ 
                        __html: line
                          .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                          .replace(/^• /, '&bull; ')
                      }} />
                    ) : (
                      line || <br />
                    )}
                  </div>
                ))}
              </div>
              <div className="message-time">
                {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </div>
            </div>
          </div>
        ))}
        
        {isLoading && (
          <div className="message amy">
            <div className="message-avatar">🤖</div>
            <div className="message-content">
              <div className="typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>
      
      {error && (
        <div className="chat-error">
          <span>⚠️ {error}</span>
        </div>
      )}
      
      <div className="chat-input">
        <div className="input-container">
          <textarea
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask AMY anything about our automations..."
            rows={1}
            disabled={isLoading}
          />
          <button 
            onClick={() => handleSendMessage()}
            disabled={!inputText.trim() || isLoading}
            className="send-button"
          >
            {isLoading ? '...' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Chat;
